import { useState } from "react";
import { publicTrees } from "@/mocks/publico";
import { PublicRiskBadge } from "./PublicBadges";

export default function PublicCtaSearch() {
  const [query, setQuery] = useState("");
  const [result, setResult] = useState<typeof publicTrees[0] | null>(null);
  const [notFound, setNotFound] = useState(false);

  const handleSearch = () => {
    setNotFound(false);
    setResult(null);
    const found = publicTrees.find(
      (t) => t.id.toLowerCase() === query.trim().toLowerCase()
    );
    if (found) {
      setResult(found);
    } else {
      setNotFound(true);
    }
  };

  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="max-w-3xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">Consulta Pública por Código CTA</h2>
          <p className="text-sm md:text-base text-foreground-600">
            Consulte uma árvore pelo código público do CTA e veja seus dados básicos, estado geral e recomendações.
          </p>
        </div>

        <div className="bg-white rounded-xl p-6 md:p-8 border border-background-200/70">
          {/* Search */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="flex-1 relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm w-4 h-4 flex items-center justify-center" />
              <input
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setNotFound(false);
                  setResult(null);
                }}
                onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
                placeholder="Ex: CTA-PUB-005"
                className="w-full pl-10 pr-4 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 focus:outline-none focus:border-primary-400 focus:bg-white transition-colors"
              />
            </div>
            <button
              onClick={handleSearch}
              className="px-6 py-2.5 text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
            >
              Consultar
            </button>
          </div>

          {/* Not found */}
          {notFound && (
            <div className="text-center py-8">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-warning-50 flex items-center justify-center">
                <i className="ri-search-line text-warning-600 text-xl w-6 h-6 flex items-center justify-center" />
              </div>
              <p className="text-sm font-medium text-foreground-800 mb-1">Código não encontrado</p>
              <p className="text-xs text-foreground-500">Verifique se o código CTA está correto. Exemplo: CTA-PUB-005</p>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="bg-background-50 rounded-xl p-5 border border-background-200/70">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
                  <i className="ri-plant-line text-primary-600 text-xl w-6 h-6 flex items-center justify-center" />
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-foreground-900">
                    {result.id} — {result.especie}
                  </h3>
                  <p className="text-xs text-foreground-500 italic mt-0.5">{result.nomeCientifico}</p>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                    <div>
                      <span className="text-xs text-foreground-400">Bairro</span>
                      <p className="text-sm font-medium text-foreground-900">{result.bairro}</p>
                    </div>
                    <div>
                      <span className="text-xs text-foreground-400">Estado geral</span>
                      <p className="text-sm font-medium text-foreground-900">{result.estado}</p>
                    </div>
                    <div>
                      <span className="text-xs text-foreground-400">Risco</span>
                      <div className="mt-0.5"><PublicRiskBadge risco={result.risco} /></div>
                    </div>
                    <div>
                      <span className="text-xs text-foreground-400">Última vistoria</span>
                      <p className="text-sm font-medium text-foreground-900">{result.ultimaVistoria}</p>
                    </div>
                  </div>

                  {result.recomendacao && (
                    <div className="mt-4 p-3 bg-warning-50 rounded-lg border border-warning-200/50">
                      <p className="text-xs text-warning-700">{result.recomendacao}</p>
                    </div>
                  )}

                  <div className="mt-4">
                    <button className="text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer">
                      Ver no mapa <i className="ri-arrow-right-line w-3 h-3 inline-flex items-center justify-center ml-0.5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}